import React, { useMemo, useState, useEffect } from 'react';
import {
  getSupportTickets,
  SupportListResult,
  SupportTicket,
  updateTicketStatus,
  getAdmins,
  reassignSupportTicket,
  AdminListItem,
  autoAssignTickets,
  escalateTicket,
  getReligions,
  getDepartments,
  transferSupportTicketTeam,
  getTicketActivities,
} from '../services/adminService';

import {
  pageContainer, cardStyle, statsGrid, labelStyle, valueStyle,
  subTextStyle, tableSearch, smallSelect, sectionTitle,
} from '../styles/theme';
import { useAdminPage, useDebounce, useToast } from '../utils/useAdminPage';
import { useNavigate } from 'react-router-dom';
import { startAdminChat } from '../services/adminChatService';
import Paginator from '../components/Paginator';
import Toast from '../components/Toast';
import { exportToCSV } from '../utils/csvExport';
import PageHeader from '../components/PageHeader';
import { getCurrentUser, isSuperAdmin } from '../utils/currentUser';

const EMPTY: SupportListResult = {
  items: [], summary: { total: 0, open: 0, resolved: 0, highPriority: 0 },
};

const RELIGION_COLORS: Record<string, { bg: string; color: string }> = {
  Islam: { bg: '#ecfdf5', color: '#065f46' },
  Hinduism: { bg: '#fef3c7', color: '#92400e' },
  Christianity: { bg: '#eff6ff', color: '#1e40af' },
  Sikhism: { bg: '#fce7f3', color: '#9d174d' },
  Buddhism: { bg: '#f5f3ff', color: '#5b21b6' },
  Jainism: { bg: '#fef2f2', color: '#991b1b' },
};

const formatDeptInfo = (dept: string) => {
  const key = dept.toUpperCase();
  const map: Record<string, { label: string; bg: string; color: string; icon: string }> = {
    SUPPORT: { label: 'CS Support', bg: '#eff6ff', color: '#1d4ed8', icon: '🎧' },
    OPERATIONS: { label: 'Operations Team', bg: '#fff7ed', color: '#c2410c', icon: '⚙️' },
    FINANCE: { label: 'Finance Team', bg: '#ecfdf5', color: '#047857', icon: '💳' },
    MARKETING: { label: 'Marketing Team', bg: '#fdf2f8', color: '#be185d', icon: '📢' },
    TECH_SUPPORT: { label: 'Tech Support', bg: '#f5f3ff', color: '#6d28d9', icon: '💻' },
    TECH: { label: 'Tech Team', bg: '#f5f3ff', color: '#6d28d9', icon: '💻' },
  };

  if (map[key]) return map[key];

  const capitalized = key.charAt(0) + key.slice(1).toLowerCase().replace('_', ' ');
  return { label: `${capitalized} Team`, bg: '#f1f5f9', color: '#334155', icon: '📁' };
};

const Support = () => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [religionFilter, setReligionFilter] = useState('');
  const [scope, setScope] = useState<'all' | 'mine'>('all');
  const [page, setPage] = useState(1);
  const [resolveTicketId, setResolveTicketId] = useState<string | null>(null);
  const [resolutionMessage, setResolutionMessage] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [ticketActivities, setTicketActivities] = useState<any[]>([]);

  // Transfer Team Modal State
  const [transferTicket, setTransferTicket] = useState<SupportTicket | null>(null);
  const [targetTeam, setTargetTeam] = useState<string>('OPERATIONS');
  const [transferNote, setTransferNote] = useState<string>('');
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [transferring, setTransferring] = useState<boolean>(false);

  const pageSize = 10;

  const dSearch = useDebounce(search, 350);
  const { toast, showToast } = useToast();
  const navigate = useNavigate();

  const currentUser = getCurrentUser();
  const canReassign = isSuperAdmin() || (currentUser.role ? ['ADMIN', 'SUPPORT', 'OPERATIONS'].includes(currentUser.role) : false);

  const cacheKey = `${dSearch}|${status}|${priority}|${teamFilter}`;

  const { data, loading, revalidating, syncLabel, lastSyncedAt, refresh, mutate } =
    useAdminPage<SupportListResult>(EMPTY, {
      cacheNs: 'support:',
      cacheKeySuffix: cacheKey,
      fetcher: () => getSupportTickets({ search: dSearch, status, priority, team: teamFilter }),
    });

  const { data: adminsData } = useAdminPage<AdminListItem[]>([], {
    cacheNs: 'support-admins:',
    cacheKeySuffix: 'list',
    fetcher: getAdmins,
  });

  const { data: religionsData } = useAdminPage<string[]>([], {
    cacheNs: 'religions:',
    cacheKeySuffix: 'list',
    fetcher: getReligions,
  });

  const { data: deptsData } = useAdminPage<string[]>(['SUPPORT', 'OPERATIONS', 'FINANCE', 'MARKETING'], {
    cacheNs: 'departments:',
    cacheKeySuffix: 'list',
    fetcher: getDepartments,
  });

  const admins = adminsData || [];
  const assignableAdmins = admins.filter((a) => a.role !== 'SUPER_ADMIN');
  const religions = religionsData || [];
  const departments = deptsData || ['SUPPORT', 'OPERATIONS', 'FINANCE', 'MARKETING'];

  useEffect(() => {
    if (selectedTicket) {
      getTicketActivities(selectedTicket.id)
        .then((res) => setTicketActivities(res))
        .catch(() => setTicketActivities([]));
    }
  }, [selectedTicket]);

  const filteredItems = useMemo(() => {
    let items = data.items;
    if (scope === 'mine' && currentUser.id) {
      items = items.filter((item) => item.assignedToAdminId === currentUser.id);
    }
    if (religionFilter) {
      items = items.filter((item) => {
        const ticketReligion = (item as any).religion || item.scholar?.religion || item.user?.religion;
        return ticketReligion === religionFilter;
      });
    }
    return items;
  }, [scope, currentUser.id, data.items, religionFilter]);

  const getAdminsForTicketReligion = (ticket: SupportTicket) => {
    const ticketReligion = (ticket as any).religion || ticket.scholar?.religion || ticket.user?.religion;
    if (!ticketReligion) return assignableAdmins;
    const matching = assignableAdmins.filter((a) => a.religion === ticketReligion);
    return matching.length > 0 ? matching : assignableAdmins;
  };

  const getAdminsForTargetTeam = (team: string) => {
    const t = team.toUpperCase();
    return assignableAdmins.filter(
      (a) => (a.role && a.role.toUpperCase() === t) || ((a as any).department && (a as any).department.toUpperCase() === t) || a.role === 'ADMIN'
    );
  };

  const summary = data.summary;
  const paged = filteredItems.slice((page - 1) * pageSize, page * pageSize);

  const handleStartChat = async (ticketId: string) => {
    try {
      await startAdminChat({ supportTicketId: ticketId });
      // Direct navigation using 8-character Ticket ID so URL ends with exact displayed Ticket ID (e.g., /messages/17854308)!
      const shortId = ticketId.length > 8 ? ticketId.slice(0, 8) : ticketId;
      navigate(`/messages/${shortId}`);
    } catch {
      showToast('Failed to start chat', 'error');
    }
  };

  const handleReassign = async (ticketId: string, assignedToAdminId: string) => {
    try {
      const adminName = admins.find((a) => a.id === assignedToAdminId)?.name || 'Admin';
      await reassignSupportTicket(ticketId, assignedToAdminId);
      showToast(`Ticket reassigned to ${adminName} successfully`);
      mutate();
    } catch {
      showToast('Failed to reassign ticket', 'error');
    }
  };

  const handleTransferTeamSubmit = async () => {
    if (!transferTicket) return;
    try {
      setTransferring(true);
      await transferSupportTicketTeam(
        transferTicket.id,
        targetTeam,
        transferNote,
        selectedAgentId || undefined
      );
      const teamObj = formatDeptInfo(targetTeam);
      showToast(`Ticket successfully transferred to ${teamObj.label}!`);
      mutate();
      setTransferTicket(null);
      setTransferNote('');
      setSelectedAgentId('');
    } catch (err: any) {
      showToast(err.message || 'Failed to transfer ticket team', 'error');
    } finally {
      setTransferring(false);
    }
  };

  const handleAutoAssign = async () => {
    try {
      const res = await autoAssignTickets();
      showToast(res.message || 'Auto-assignment complete');
      mutate();
    } catch {
      showToast('Failed to auto-assign tickets', 'error');
    }
  };

  const handleEscalate = async (ticketId: string) => {
    try {
      const res = await escalateTicket(ticketId);
      const dispute = res?.data;
      const assignedAdminName = dispute?.assignedAdmin?.name || 'an administrator';
      showToast(`Ticket escalated to dispute and assigned to ${assignedAdminName}`);
      mutate();
    } catch {
      showToast('Failed to escalate ticket', 'error');
    }
  };

  const handleExportCSV = () => {
    const exportData = filteredItems.map((t: any) => ({
      TicketID: t.id.slice(0, 8),
      FullID: t.id,
      Subject: t.subject,
      AssignedTeam: t.assignedTeam || 'SUPPORT',
      Category: t.category || 'N/A',
      Priority: t.priority,
      Status: t.status,
      Religion: t.religion || 'N/A',
      User: t.user?.name || t.scholar?.user?.name || 'N/A',
      AssignedTo: t.assignedToAdmin?.name || 'Unassigned',
      CreatedAt: new Date(t.createdAt).toLocaleString(),
      ResolvedAt: t.resolvedAt ? new Date(t.resolvedAt).toLocaleString() : 'N/A',
    }));
    exportToCSV(exportData, 'Support_Tickets_Report');
  };

  const stats = [
    { label: 'Total Tickets', val: filteredItems.length.toLocaleString(), sub: scope === 'mine' ? 'Assigned to me' : 'All tickets', color: '#2563eb' },
    { label: 'Open', val: summary.open.toString(), sub: 'Awaiting resolution', color: '#f59e0b' },
    { label: 'High Priority', val: summary.highPriority.toString(), sub: 'Needs attention', color: '#dc2626' },
    { label: 'Resolved', val: summary.resolved.toString(), sub: 'Completed', color: '#10b981' },
  ];

  const priorityBadge = (p: string) => ({ HIGH: { bg: '#fef2f2', color: '#b91c1c' }, MEDIUM: { bg: '#fffbeb', color: '#b45309' }, LOW: { bg: '#f0fdf4', color: '#15803d' } }[p] || { bg: '#f1f5f9', color: '#475569' });
  const statusBadge = (s: string) => ({ OPEN: { bg: '#eff6ff', color: '#1e40af' }, IN_PROGRESS: { bg: '#fffbeb', color: '#b45309' }, RESOLVED: { bg: '#f0fdf4', color: '#15803d' }, CLOSED: { bg: '#f1f5f9', color: '#475569' } }[s] || { bg: '#f1f5f9', color: '#475569' });

  const religionBadge = (r?: string) => {
    if (!r) return null;
    const c = RELIGION_COLORS[r] || { bg: '#f1f5f9', color: '#475569' };
    return <span style={{ padding: '3px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: c.bg, color: c.color }}>{r}</span>;
  };

  const teamBadge = (team?: string) => {
    const key = (team || 'SUPPORT').toUpperCase();
    const cfg = formatDeptInfo(key);
    return (
      <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 800, background: cfg.bg, color: cfg.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span>{cfg.icon}</span> {cfg.label}
      </span>
    );
  };

  return (
    <div style={pageContainer}>
      <Toast toast={toast} />
      <PageHeader
        breadcrumb="Dashboard > Support"
        title="Support Tickets"
        subtitle="Monitor, assign, route, and resolve customer and scholar support requests across CS Support, Operations, Tech Support, and Finance teams."
        syncLabel={syncLabel} revalidating={revalidating} loading={loading}
        lastSyncedAt={lastSyncedAt} onRefresh={refresh} onExport={handleExportCSV}
      />

      <div style={statsGrid}>
        {stats.map((s, i) => (
          <div key={i} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><span style={labelStyle}>{s.label}</span></div>
            <div style={valueStyle}>{s.val}</div>
            <div style={subTextStyle}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={cardStyle}>
        {/* Dynamic Team Filter Pills Bar (Auto-fetches Departments) */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 18, overflowX: 'auto', paddingBottom: 6 }}>
          <button
            onClick={() => { setTeamFilter(''); setPage(1); }}
            style={{
              border: teamFilter === '' ? '2px solid #2563eb' : '1px solid #e2e8f0',
              background: teamFilter === '' ? '#eff6ff' : '#ffffff',
              color: teamFilter === '' ? '#1d4ed8' : '#64748b',
              fontWeight: teamFilter === '' ? 800 : 600,
              fontSize: 13,
              padding: '8px 16px',
              borderRadius: 24,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span>🌐</span> All Teams
          </button>
          {departments.map((d) => {
            const isActive = teamFilter.toUpperCase() === d.toUpperCase();
            const info = formatDeptInfo(d);
            return (
              <button
                key={d}
                onClick={() => { setTeamFilter(d); setPage(1); }}
                style={{
                  border: isActive ? '2px solid #2563eb' : '1px solid #e2e8f0',
                  background: isActive ? '#eff6ff' : '#ffffff',
                  color: isActive ? '#1d4ed8' : '#64748b',
                  fontWeight: isActive ? 800 : 600,
                  fontSize: 13,
                  padding: '8px 16px',
                  borderRadius: 24,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{info.icon}</span> {info.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <input
            type="text" placeholder="Search tickets by subject, description, user…"
            style={{ ...tableSearch, flex: 1, minWidth: 220 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <select style={smallSelect} value={scope} onChange={e => { setScope(e.target.value as 'all' | 'mine'); setPage(1); }}>
              <option value="all">All Tickets</option>
              <option value="mine">My Assigned Tickets</option>
            </select>
            <select style={smallSelect} value={religionFilter} onChange={e => { setReligionFilter(e.target.value); setPage(1); }}>
              <option value="">All Religions</option>
              {religions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select style={smallSelect} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All Status</option><option value="OPEN">Open</option><option value="IN_PROGRESS">In Progress</option><option value="RESOLVED">Resolved</option><option value="CLOSED">Closed</option>
            </select>
            <select style={smallSelect} value={priority} onChange={e => { setPriority(e.target.value); setPage(1); }}>
              <option value="">All Priority</option><option value="HIGH">High</option><option value="MEDIUM">Medium</option><option value="LOW">Low</option>
            </select>
            {canReassign && (
              <button
                onClick={handleAutoAssign}
                title="Automatically assigns unassigned tickets to religion-matching admins by least workload"
                style={{ border: 'none', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 700, fontSize: 12, padding: '0 16px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                ⚡ Auto-Assign by Religion
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ ...sectionTitle, margin: 0 }}>
            {teamFilter ? `${formatDeptInfo(teamFilter).label} Tickets` : 'All Support Tickets'}
          </h3>
        </div>

        {/* Sleek, World-Class Professional Table */}
        <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', color: '#475569', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '14px 18px' }}>Ticket & Subject</th>
                <th style={{ padding: '14px 18px' }}>User / Scholar</th>
                <th style={{ padding: '14px 18px' }}>Team</th>
                <th style={{ padding: '14px 18px' }}>Assignee</th>
                <th style={{ padding: '14px 18px' }}>Priority</th>
                <th style={{ padding: '14px 18px' }}>Status</th>
                <th style={{ padding: '14px 18px' }}>Created</th>
                <th style={{ padding: '14px 18px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading tickets…</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No tickets found.</td></tr>
              ) : paged.map((t) => {
                const pb = priorityBadge(t.priority);
                const sb = statusBadge(t.status);
                const ticketReligion = (t as any).religion || t.scholar?.religion || t.user?.religion;
                const filteredAdmins = getAdminsForTicketReligion(t);
                const formattedTicketId = t.id.slice(0, 8);

                return (
                  <tr
                    key={t.id}
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.15s ease' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    onClick={() => setSelectedTicket(t)}
                  >
                    {/* Ticket & Subject */}
                    <td style={{ padding: '14px 18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{t.subject || 'Support Request'}</span>
                        <span style={{ fontSize: 10, fontWeight: 800, background: '#eef2ff', color: '#4338ca', padding: '2px 8px', borderRadius: 6, letterSpacing: '0.4px', fontFamily: 'monospace' }}>
                          #{formattedTicketId}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                        Category: <strong style={{ color: '#475569' }}>{t.category || 'OTHER'}</strong>
                      </div>
                    </td>

                    {/* User / Scholar & Religion */}
                    <td style={{ padding: '14px 18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>{t.user?.name || t.scholar?.user?.name || 'Customer'}</div>
                        {religionBadge(ticketReligion)}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{t.user?.email || t.scholar?.user?.email || '—'}</div>
                    </td>

                    {/* Team */}
                    <td style={{ padding: '14px 18px' }}>
                      {teamBadge((t as any).assignedTeam)}
                    </td>

                    {/* Assignee */}
                    <td style={{ padding: '14px 18px' }} onClick={e => e.stopPropagation()}>
                      {canReassign ? (
                        <select
                          style={{
                            padding: '4px 10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            background: '#ffffff',
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#1e293b',
                            cursor: 'pointer',
                            outline: 'none',
                          }}
                          value={t.assignedToAdminId || ''}
                          onChange={e => e.target.value && handleReassign(t.id, e.target.value)}
                        >
                          <option value="">Unassigned</option>
                          {filteredAdmins.map((admin) => (
                            <option key={admin.id} value={admin.id}>
                              {admin.name}{admin.religion ? ` (${admin.religion})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span style={{ fontWeight: 600, color: '#334155' }}>{t.assignedToAdmin?.name || 'Unassigned'}</span>
                      )}
                    </td>

                    {/* Priority */}
                    <td style={{ padding: '14px 18px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 800, background: pb.bg, color: pb.color }}>
                        {t.priority || 'LOW'}
                      </span>
                    </td>

                    {/* Status */}
                    <td style={{ padding: '14px 18px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 800, background: sb.bg, color: sb.color }}>
                        {t.status}
                      </span>
                    </td>

                    {/* Created */}
                    <td style={{ padding: '14px 18px' }}>
                      <div style={{ fontSize: 12, color: '#334155', fontWeight: 600 }}>{new Date(t.createdAt).toLocaleDateString()}</div>
                      {t.resolvedAt && (
                        <div style={{ fontSize: 10, color: '#10b981', fontWeight: 700 }}>
                          Resolved {new Date(t.resolvedAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '14px 18px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                        {/* Primary Chat Button */}
                        <button
                          onClick={() => handleStartChat(t.id)}
                          style={{
                            border: 'none',
                            background: 'linear-gradient(135deg, #4f46e5, #3730a3)',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: 12,
                            padding: '7px 14px',
                            borderRadius: 8,
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(79, 70, 229, 0.2)',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          💬 Chat
                        </button>

                        {/* Secondary Move Team */}
                        <button
                          onClick={() => { setTransferTicket(t); setTargetTeam('OPERATIONS'); setTransferNote(''); setSelectedAgentId(''); }}
                          style={{ border: '1px solid #fed7aa', background: '#fff7ed', color: '#c2410c', fontWeight: 700, fontSize: 12, padding: '7px 12px', borderRadius: 8, cursor: 'pointer' }}
                          title="Move ticket to Operations, Tech, or Finance team"
                        >
                          🔄 Move
                        </button>

                        {/* Resolve Button */}
                        {t.status !== 'RESOLVED' && t.status !== 'CLOSED' && (
                          <button
                            onClick={() => setResolveTicketId(t.id)}
                            style={{ border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', fontWeight: 700, fontSize: 12, padding: '7px 12px', borderRadius: 8, cursor: 'pointer' }}
                          >
                            Resolve
                          </button>
                        )}

                        {/* Escalate Button */}
                        {t.status !== 'RESOLVED' && t.status !== 'CLOSED' && (
                          <button
                            onClick={() => handleEscalate(t.id)}
                            style={{ border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontWeight: 700, fontSize: 12, padding: '7px 10px', borderRadius: 8, cursor: 'pointer' }}
                            title="Escalate to dispute"
                          >
                            ⚡
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Paginator
          total={filteredItems.length}
          page={page} pageSize={pageSize} onPage={setPage}
        />
      </div>

      {/* Transfer Team Modal */}
      {transferTicket && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
          <div style={{ ...cardStyle, width: 480, maxWidth: '95%' }}>
            <h3 style={{ margin: '0 0 4px 0', fontSize: 18 }}>🔄 Move Ticket to Different Team</h3>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
              Escalate or re-route ticket <strong>#{transferTicket.id.slice(0, 8)}</strong> to another specialized team.
            </p>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Target Destination Team</label>
              <select
                value={targetTeam}
                onChange={e => setTargetTeam(e.target.value)}
                style={{ width: '100%', height: 40, borderRadius: 8, border: '1px solid #cbd5e1', padding: '0 12px', fontWeight: 700 }}
              >
                {departments.map((d) => {
                  const info = formatDeptInfo(d);
                  return (
                    <option key={d} value={d}>{info.icon} {info.label}</option>
                  );
                })}
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Assign Agent (Optional - Auto-assigns if left default)</label>
              <select
                value={selectedAgentId}
                onChange={e => setSelectedAgentId(e.target.value)}
                style={{ width: '100%', height: 40, borderRadius: 8, border: '1px solid #cbd5e1', padding: '0 12px' }}
              >
                <option value="">⚡ Auto-assign to active online team agent</option>
                {getAdminsForTargetTeam(targetTeam).map(a => (
                  <option key={a.id} value={a.id}>{a.name} ({a.role})</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Transfer Reason / Internal Note</label>
              <textarea
                value={transferNote}
                onChange={e => setTransferNote(e.target.value)}
                placeholder="Explain why this ticket is being moved..."
                style={{ width: '100%', boxSizing: 'border-box', minHeight: 80, padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setTransferTicket(null)}
                disabled={transferring}
                style={{ border: '1px solid #e2e8f0', background: '#f8fafc', padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                onClick={handleTransferTeamSubmit}
                disabled={transferring}
                style={{ border: 'none', background: 'linear-gradient(135deg, #c2410c, #ea580c)', color: '#fff', padding: '9px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}
              >
                {transferring ? 'Moving Team…' : 'Confirm Team Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resolveTicketId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ ...cardStyle, width: 420, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18 }}>Resolve Ticket</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Resolution Message</label>
              <textarea value={resolutionMessage} onChange={e => setResolutionMessage(e.target.value)} placeholder="Enter resolution message..." style={{ width: '100%', boxSizing: 'border-box', minHeight: 100, padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => { setResolveTicketId(null); setResolutionMessage(''); }} style={{ border: 'none', background: '#f1f5f9', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button
                onClick={async () => {
                  if (!resolutionMessage.trim()) return showToast('Resolution message is required', 'error');
                  try {
                    await updateTicketStatus(resolveTicketId, 'RESOLVED', resolutionMessage, currentUser.id || undefined);
                    showToast('Ticket resolved successfully');
                    mutate();
                    setResolveTicketId(null);
                    setResolutionMessage('');
                  } catch {
                    showToast('Failed to resolve ticket', 'error');
                  }
                }}
                style={{ border: 'none', background: '#2563eb', color: '#fff', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
              >
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedTicket && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={e => { if (e.target === e.currentTarget) setSelectedTicket(null); }}>
          <div style={{ background: 'hsl(var(--card))', borderRadius: 14, width: 'min(95vw, 620px)', maxHeight: '88vh', overflowY: 'auto', border: '1px solid hsl(var(--border))', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ marginTop: 0, marginBottom: 4 }}>{selectedTicket.subject || 'Untitled Ticket'}</h3>
                <div style={{ fontSize: 12, color: '#64748b' }}>Ticket #{selectedTicket.id.slice(0, 8)}</div>
              </div>
              <div>{teamBadge((selectedTicket as any).assignedTeam)}</div>
            </div>

            {selectedTicket.description && <p style={{ marginTop: 12, background: '#f8fafc', padding: 12, borderRadius: 8, color: '#334155', border: '1px solid #f1f5f9' }}>{selectedTicket.description}</p>}
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13, marginTop: 14 }}>
              <div><strong>User / Scholar:</strong> {selectedTicket.user?.name || selectedTicket.scholar?.user?.name || '—'}</div>
              <div><strong>Assigned Agent:</strong> {selectedTicket.assignedToAdmin?.name || 'Unassigned'}</div>
              <div><strong>Status:</strong> {selectedTicket.status}</div>
              <div><strong>Priority:</strong> {selectedTicket.priority}</div>
              <div><strong>Religion:</strong> {religionBadge((selectedTicket as any).religion) || '—'}</div>
              <div><strong>Created At:</strong> {new Date(selectedTicket.createdAt).toLocaleString()}</div>
            </div>

            {/* Audit History Timeline */}
            {ticketActivities.length > 0 && (
              <div style={{ marginTop: 18, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b' }}>Ticket Activity & Transfer History</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 180, overflowY: 'auto' }}>
                  {ticketActivities.map((act) => (
                    <div key={act.id} style={{ background: '#f8fafc', borderLeft: '3px solid #6366f1', padding: '8px 12px', borderRadius: '0 8px 8px 0', fontSize: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569', fontWeight: 600 }}>
                        <span>{act.actor?.name || 'System'} ({act.action})</span>
                        <span style={{ fontSize: 10, color: '#94a3b8' }}>{new Date(act.createdAt).toLocaleString()}</span>
                      </div>
                      {act.details && <div style={{ color: '#0f172a', marginTop: 4 }}>{act.details}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setSelectedTicket(null)} style={{ border: '1px solid #e2e8f0', background: '#fff', padding: '8px 14px', borderRadius: 8, cursor: 'pointer' }}>Close</button>
              <button onClick={() => { setTransferTicket(selectedTicket); setSelectedTicket(null); setTargetTeam('OPERATIONS'); setTransferNote(''); setSelectedAgentId(''); }} style={{ border: 'none', background: '#fff7ed', color: '#c2410c', padding: '8px 14px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>🔄 Move Team</button>
              <button onClick={() => { setSelectedTicket(null); handleStartChat(selectedTicket.id); }} style={{ border: 'none', background: '#eff6ff', color: '#1e40af', padding: '8px 14px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Open Chat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
